/**
 * PublicBadgePage — no-auth page rendered when someone scans a badge QR code
 * (blueprint §4, compliance requirement B: scan shows the ACTUAL cert documents).
 * Fetches GET /api/badge/public/:qrCode; each cert with a documentUrl links
 * directly at the relative path the API returns (append nothing).
 * Opened from a phone scan → clean mobile-first layout.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, FileText, AlertTriangle } from 'lucide-react';
import { api } from '../utils/api';
import { badgeUrl } from '../components/CertBadge';
// Badge reaudit R9: calendar-date formatting for cert expirations.
import { formatCalendarDate } from '../utils/calendarDate';

interface PublicCert {
  certType: string;
  certName: string;
  expirationDate: string | null;
  documentUrl?: string | null;
}

interface PublicBadge {
  name: string;
  role: string;
  companyName: string;
  photoUrl: string | null;
  certCount: number;
  validCertNames: string[];
  expiredCertNames: string[];
  certifications: PublicCert[];
}

function isExpired(d: string | null): boolean {
  if (!d) return false;
  return new Date(d).getTime() < Date.now();
}

export default function PublicBadgePage() {
  const { qrCode } = useParams<{ qrCode: string }>();
  const [badge, setBadge] = useState<PublicBadge | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    setRateLimited(false);
    setError('');
    api
      .get(`/badge/public/${encodeURIComponent(qrCode ?? '')}`)
      .then((res: any) => {
        // Server wraps ALL JSON as {success, data} — unwrap res.data.data.
        // (res.data is the wrapper, NOT the badge — using it directly left
        // certifications/validCertNames undefined → .length threw → blank page.)
        const payload = res?.data?.data ?? res?.data;
        if (alive) setBadge(payload as PublicBadge);
      })
      .catch((e: any) => {
        if (!alive) return;
        if (e?.response?.status === 404) setNotFound(true);
        else if (e?.response?.status === 429) setRateLimited(true);
        else setError('Something went wrong. Please try again.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [qrCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-slate-500 text-sm">Verifying badge…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow border border-gray-200 p-8 text-center max-w-sm">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900">Badge not found</h1>
          <p className="text-sm text-gray-500 mt-1">
            This badge code is invalid or no longer active. Ask the employee for a current badge.
          </p>
        </div>
      </div>
    );
  }

  if (rateLimited) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow border border-gray-200 p-8 text-center max-w-sm">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900">Too many requests</h1>
          <p className="text-sm text-gray-500 mt-1">Please wait a moment and scan the badge again.</p>
        </div>
      </div>
    );
  }

  // Guard: an unexpected payload shape (e.g. the raw {success, data} wrapper)
  // must show the error state instead of a white screen from .length on undefined.
  const badgeOk =
    !!badge &&
    typeof badge.name === 'string' &&
    Array.isArray(badge.certifications) &&
    Array.isArray(badge.validCertNames) &&
    Array.isArray(badge.expiredCertNames);
  if (error || !badgeOk) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow border border-gray-200 p-8 text-center max-w-sm">
          <h1 className="text-lg font-bold text-gray-900">Unable to load badge</h1>
          <p className="text-sm text-gray-500 mt-1">{error || 'Please try again.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow border border-gray-200 overflow-hidden">
          <div className="bg-green-600 text-white px-4 py-2 flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4" />
            Verified employee badge
          </div>

          <div className="p-5">
            <div className="flex items-center gap-3">
              {badge.photoUrl ? (
                <img src={badge.photoUrl} alt={badge.name} className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-slate-200 flex items-center justify-center text-lg font-semibold text-slate-600">
                  {badge.name
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase() ?? '')
                    .join('')}
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{badge.name}</h1>
                {badge.role && <p className="text-sm text-gray-600">{badge.role}</p>}
                {badge.companyName && <p className="text-sm text-gray-400">{badge.companyName}</p>}
              </div>
            </div>

            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Certifications</h2>
                <span className="text-xs text-gray-400">
                  {badge.validCertNames.length} valid
                  {badge.expiredCertNames.length > 0 ? ` · ${badge.expiredCertNames.length} expired` : ''}
                </span>
              </div>

              {badge.certifications.length === 0 ? (
                <p className="text-sm text-gray-400 py-3">No certifications on file.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {badge.certifications.map((c, i) => {
                    const expired = isExpired(c.expirationDate);
                    return (
                      <li key={i} className="py-3 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 break-words">{c.certName}</p>
                          <p className="text-xs text-gray-500">
                            {c.certType ? `${c.certType} · ` : ''}
                            Expires:{' '}
                            {c.expirationDate ? formatCalendarDate(c.expirationDate) : 'No expiration'}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {expired && (
                            <span data-testid="expired-chip" className="inline-block rounded text-[10px] font-bold px-1.5 py-0.5 bg-red-100 text-red-700">
                              EXPIRED
                            </span>
                          )}
                          {c.documentUrl && (
                            <a
                              href={c.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 underline"
                              data-testid="view-document"
                            >
                              <FileText className="h-3.5 w-3.5" />
                              View document
                            </a>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <p className="mt-5 text-[11px] text-gray-400 break-all">
              Badge verification link: {badgeUrl(qrCode ?? '')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
